import { useRef, useCallback } from 'react';
import type React from 'react';
import { graphic } from 'echarts';
import type { ECharts } from 'echarts';
import type { EChartsBaseHandle } from 'components/Charts/EChartsBase';
import type { ChartDataItem } from 'utils/chart-options';
import { findStackedAreaFieldAtY } from './stackedAreaHover';

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

// Tracks which stacked area band the cursor is in by combining a capture-phase mouse handler
// with convertFromPixel + cumulative stacking math. Writes to hoveredFieldNameRef so the tooltip
// formatter can read it synchronously, and dispatches highlight/downplay to drive emphasis styles.
// Manual dispatch replaces `emphasis: { focus: 'series' }` on the series options, which causes
// flicker because ECharts' canvas hit-testing on area fills is unreliable.
export function useHoveredStackedAreaField(
  chartRef: React.RefObject<EChartsBaseHandle | null>,
  chartData: ChartDataItem[] | undefined,
  chartFields: string[],
  stacked: boolean,
  totalTooltipField: string | undefined,
  clipHoverLineToArea: boolean,
  hoverLineColor: string,
): {
  hoveredFieldNameRef: React.MutableRefObject<string | null>;
  onMouseMoveCapture: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave: () => void;
} {
  const hoveredFieldNameRef = useRef<string | null>(null);
  const highlightedFieldRef = useRef<string | null>(null);
  // The guide is a ZRender element owned by one chart instance's zr, and it is cached because it
  // is re-attributed on every mousemove. echarts-for-react disposes and re-inits on some updates,
  // and MultilineChart unmounts EChartsBase outright whenever it renders without an option, both
  // of which destroy that zr — after which `attr()` on the cached element is a silent no-op and
  // the guide stops drawing for good. So the cache is keyed on the zr that owns it and
  // self-invalidates, rather than on a ready subscription, which would have to be re-registered
  // every time the chart child remounts. The element needs no explicit teardown: its lifetime is
  // exactly its zr's, and this hook cannot outlive the chart it belongs to.
  const hoverLineRef = useRef<{ line: InstanceType<typeof graphic.Line>; zr: ReturnType<ECharts['getZr']> } | null>(
    null,
  );

  const getHoverLine = useCallback(
    (instance: ECharts) => {
      const zr = instance.getZr();
      const cached = hoverLineRef.current;
      if (cached?.zr === zr) return cached.line;

      const line = new graphic.Line({
        silent: true,
        z: 100,
        invisible: true,
        shape: { x1: 0, y1: 0, x2: 0, y2: 0 },
        style: { stroke: hoverLineColor, lineWidth: 1 },
      });
      zr.add(line);
      hoverLineRef.current = { line, zr };
      return line;
    },
    [hoverLineColor],
  );

  const hideHoverLine = useCallback(() => {
    if (!clipHoverLineToArea) return;
    hoverLineRef.current?.line.attr({ invisible: true });
  }, [clipHoverLineToArea]);

  const drawHoverLine = useCallback(
    (xIdx: number, lowerAreaBound: number, upperAreaBound: number) => {
      if (!clipHoverLineToArea) return;

      // One guarded raw scope rather than per-call guards: the guide needs getZr() alongside the
      // coordinate conversions, and all of them must see the same instance. withChart runs this
      // only against a live chart and swallows the lifecycle throws, so a frame during
      // (re)initialization simply draws nothing and the next mousemove draws normally.
      chartRef.current?.withChart((instance) => {
        const lowerPoint = instance.convertToPixel({ seriesIndex: 0 }, [xIdx, lowerAreaBound]);
        const upperPoint = instance.convertToPixel({ seriesIndex: 0 }, [xIdx, upperAreaBound]);
        if (!Array.isArray(lowerPoint) || !Array.isArray(upperPoint)) return;

        // ECharts can place y=0 outside the visible scale (for example, a positive-only chart
        // whose value axis starts above zero). Find the plot's visible vertical bounds through
        // the public containPixel API, then clamp the renderer-level guide to those bounds.
        const findGridEdge = (low: number, high: number, edge: 'top' | 'bottom'): number => {
          let lower = low;
          let upper = high;
          for (let i = 0; i < 12; i += 1) {
            const middle = (lower + upper) / 2;
            if (instance.containPixel({ gridIndex: 0 }, [upperPoint[0], middle])) {
              if (edge === 'top') upper = middle;
              else lower = middle;
            } else if (edge === 'top') lower = middle;
            else upper = middle;
          }
          return edge === 'top' ? upper : lower;
        };
        const pointInGrid = upperPoint[1];
        const gridTop = findGridEdge(0, pointInGrid, 'top');
        const gridBottom = findGridEdge(pointInGrid, instance.getHeight(), 'bottom');

        getHoverLine(instance).attr({
          invisible: false,
          shape: {
            x1: upperPoint[0],
            y1: clamp(lowerPoint[1], gridTop, gridBottom),
            x2: upperPoint[0],
            y2: clamp(upperPoint[1], gridTop, gridBottom),
          },
        });
      });
    },
    [chartRef, clipHoverLineToArea, getHoverLine],
  );

  const dispatchHighlight = useCallback(
    (field: string | null) => {
      // Downplay the previous series only when switching to a different one.
      if (highlightedFieldRef.current !== field && highlightedFieldRef.current) {
        chartRef.current?.dispatchAction({ type: 'downplay', seriesName: highlightedFieldRef.current });
      }
      highlightedFieldRef.current = field;
      // Always re-dispatch highlight even when the field hasn't changed — ECharts resets
      // emphasis state whenever the axis pointer moves to a new x column.
      if (field) {
        chartRef.current?.dispatchAction({ type: 'highlight', seriesName: field });
      }
    },
    [chartRef],
  );

  const onMouseMoveCapture = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!chartData?.length || !chartFields?.length) {
        hoveredFieldNameRef.current = null;
        dispatchHighlight(null);
        hideHoverLine();
        return;
      }
      const chart = chartRef.current;
      const chartDom = chart?.getDom();
      if (!chart || !chartDom) return;

      const rect = chartDom.getBoundingClientRect();
      const pixelX = e.clientX - rect.left;
      const pixelY = e.clientY - rect.top;
      if (pixelX < 0 || pixelY < 0 || pixelX > rect.width || pixelY > rect.height) {
        hoveredFieldNameRef.current = null;
        dispatchHighlight(null);
        hideHoverLine();
        return;
      }

      // null while the chart is (re)initializing — treated the same as "cursor off the plot".
      const dataCoords = chart.convertFromPixel({ seriesIndex: 0 }, [pixelX, pixelY]);
      if (!dataCoords) {
        hoveredFieldNameRef.current = null;
        dispatchHighlight(null);
        hideHoverLine();
        return;
      }

      const xIdx = Math.max(0, Math.min(Math.round(dataCoords[0] as number), chartData.length - 1));
      const dataY = dataCoords[1] as number;
      const dataPoint = chartData[xIdx];

      // ECharts' axis pointer spans the entire grid. The replacement line should occupy only
      // the filled area, between its zero baseline and its data boundary. Positive and negative
      // stack values accumulate independently, while non-stacked series use their outer values.
      let lowerAreaBound = 0;
      let upperAreaBound = 0;
      for (const field of chartFields) {
        if (field === totalTooltipField) continue;
        const value = dataPoint[field];
        if (typeof value !== 'number') continue;
        if (stacked) {
          if (value < 0) lowerAreaBound += value;
          else upperAreaBound += value;
        } else {
          lowerAreaBound = Math.min(lowerAreaBound, value);
          upperAreaBound = Math.max(upperAreaBound, value);
        }
      }
      drawHoverLine(xIdx, lowerAreaBound, upperAreaBound);

      if (!stacked) {
        hoveredFieldNameRef.current = null;
        dispatchHighlight(null);
        return;
      }

      const targetField = findStackedAreaFieldAtY(dataPoint, chartFields, totalTooltipField, dataY);

      hoveredFieldNameRef.current = targetField;
      dispatchHighlight(targetField);
    },
    [stacked, chartData, chartFields, totalTooltipField, chartRef, dispatchHighlight, drawHoverLine, hideHoverLine],
  );

  const onMouseLeave = useCallback(() => {
    hoveredFieldNameRef.current = null;
    dispatchHighlight(null);
    hideHoverLine();
  }, [dispatchHighlight, hideHoverLine]);

  return { hoveredFieldNameRef, onMouseMoveCapture, onMouseLeave };
}
